import React, { FC } from 'react';
import { Typography, colors } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate, withTranslate } from 'react-admin';
import { time } from '~/utils';
import { useReferenceEntity } from '~/hooks';
import { getName } from '~/utils/identities';
import { removeDraftPostfix } from '~/hooks/draft';
import CopyButton from '../components/CopyButton';

const useStyles = makeStyles(() => {
  return {
    root: {
      display: 'flex',
    },
    avatarWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '10px',
      '& > div': {
        width: 50,
        height: 50,
        '& svg': {
          fontSize: '1.7rem',
        },
      },
    },
    headline: {
      marginTop: '8px',
    },
    subtitle: {
      lineHeight: '20px',
      paddingBottom: '7px',
      paddingLeft: '1px',
      color: colors.blueGrey[500],
    },
    subtitleUuidRow: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    subtitleUuid: {
      marginLeft: '4px',
    },
    subtitleCopyButton: {
      padding: 0,
      marginLeft: '4px',
      color: 'inherit',
    },
    subtitleCopyIcon: {
      fontSize: '0.875rem',
    },
  };
});

interface DefaultSubtitleProps {
  record: any;
  resource: string;
  translate?: any;
  classes?: ReturnType<typeof useStyles>;
}

const DefaultSubtitle: FC<DefaultSubtitleProps> = (props) => {
  const { record, resource } = props;
  const created = record.created || record.createdAt || record.timestamp || record.submittedAt;
  const updated = record.updated || record.updatedAt || record.lastUpdated || record.lastUpdatedAt;
  const lastUpdatedBy =
    typeof record.lastUpdatedBy === 'string'
      ? {
          id: record.lastUpdatedBy,
          type: 'DASHBOARD_IDENTITY',
        }
      : {
          id: '',
          type: 'SYSTEM',
        };

  const { data, isLoading: isLoadingLastUpdatedBy } = useReferenceEntity(lastUpdatedBy, true);
  const originatorName = getName?.[lastUpdatedBy.type]?.(data ?? {}) ?? lastUpdatedBy.id;

  const lastUpdatedDate = time(record.lastUpdatedAt).format(dateMomentFormat);
  const lastUpdatedTime = time(record.lastUpdatedAt).format(timeMomentFormat);

  const translate = useTranslate();
  const label = (key: string, opts?: any) => translate(`components.RecordTitle.${key}`, opts);

  const classes = useStyles();
  return (
    <>
      {record.id && (
        <span className={classes.subtitleUuidRow}>
          {`${translate(`resources.${resource}.name`, 1)} `}
          <span className={classes.subtitleUuid}>{removeDraftPostfix(record.id)}</span>
          <CopyButton
            value={record.id}
            classes={{ button: classes.subtitleCopyButton, icon: classes.subtitleCopyIcon }}
          />
        </span>
      )}
      {record.id && created && ', '}
      {created && `${label('createdOn')} ${dateFormat(created)}.`}{' '}
      {updated && lastUpdatedBy.id
        ? `${label('lastUpdatedBy', {
            date: lastUpdatedDate,
            time: lastUpdatedTime,
            by: lastUpdatedBy.id && isLoadingLastUpdatedBy ? label('loading') : originatorName,
          })}`
        : updated
        ? `${label('lastUpdated', { date: lastUpdatedDate, time: lastUpdatedTime })}`
        : ''}
    </>
  );
};

interface ShowTitleProps {
  record: any;
  resource: string;
  headline: (record: any, resource: string, translate: any) => React.ReactNode;
  subtitle?: (record: any, resource: string, translate: any) => React.ReactNode;
  translate: any;
  icon: React.ReactNode;
  iconFn: (record: any, resource: string) => React.ReactNode;
}

const DefaultSubtitleFn = () => '';

const ShowTitle: FC<ShowTitleProps> = ({
  record,
  resource,
  headline,
  subtitle = DefaultSubtitleFn,
  translate,
  icon,
  iconFn,
}) => {
  const classes = useStyles();

  return record ? (
    <div className={classes.root}>
      {icon && <div className={classes.avatarWrapper}>{icon}</div>}
      {iconFn && <div className={classes.avatarWrapper}>{iconFn(record, resource)}</div>}
      <div>
        {headline ? (
          <Typography variant='h2' className={classes.headline}>
            {headline(record, resource, translate)}
          </Typography>
        ) : null}
        {subtitle ? (
          <Typography variant='subtitle1' className={classes.subtitle}>
            {subtitle !== DefaultSubtitleFn ? (
              <>{subtitle(record, resource, translate)}</>
            ) : (
              <DefaultSubtitle record={record} resource={resource} translate={translate} />
            )}
          </Typography>
        ) : null}
      </div>
    </div>
  ) : (
    <div />
  );
};

const dateMomentFormat = 'l';
const timeMomentFormat = 'LT';
const dateFormat = (dateString: string) => {
  return `${time(dateString).format(dateMomentFormat)} ${time(dateString).format(
    timeMomentFormat
  )}`;
};

export default withTranslate(ShowTitle);
